// Catalogue descriptif des 25 modules (D-080) : ce que fait chaque module, comment le montrer, avec
// quels comptes de présentation, sur quels écrans, et son état de livraison. Lu par l'écran du super
// administrateur (activation globale) et servi par l'API. Les fiches sont dans deux fichiers voisins.
import { z } from 'zod';
import { CODES_MODULES, MODULES, type CodeModule } from './administration';
import { FICHES_M01_M13 } from './modules-catalogue-1';
import { FICHES_M14_M25 } from './modules-catalogue-2';

/** État de livraison : livré (API et écrans recettés), partiel (une partie livrée), prévu (lot ultérieur). */
export const ETATS_LIVRAISON = ['livre', 'partiel', 'prevu'] as const;
export type EtatLivraison = (typeof ETATS_LIVRAISON)[number];

export interface FicheModule {
  /** Lot de livraison principal (L0 à L7). */
  lot: string;
  etat: EtatLivraison;
  /** Une phrase : à quoi sert le module. */
  resume: string;
  /** Ce que fait exactement le module, par phrases courtes. */
  description: string[];
  /** Étapes d'une démonstration ou d'une utilisation courante, dans l'ordre. */
  utilisation: string[];
  /** Profils de présentation à utiliser (identifiants de la commune de Mbeng-Ntem ou comptes transversaux). */
  comptes: string[];
  /** Chemins des écrans concernés dans l'application. */
  ecrans: string[];
}

export const CATALOGUE_MODULES: Record<CodeModule, FicheModule> = { ...FICHES_M01_M13, ...FICHES_M14_M25 };

export const LIBELLES_ETAT_LIVRAISON: Record<EtatLivraison, string> = {
  livre: 'Livré',
  partiel: 'Partiel',
  prevu: 'Prévu',
};

/** Fiche complète d'un module : code, libellé et catalogue. */
export function ficheModule(code: CodeModule): FicheModule & { code: CodeModule; libelle: string } {
  return { code, libelle: MODULES[code], ...CATALOGUE_MODULES[code] };
}

export const schemaCodeModule = z.enum(CODES_MODULES as [CodeModule, ...CodeModule[]]);

/** Activation ou désactivation globale (plateforme) ou dans une collectivité, par le super administrateur. */
export const schemaModificationModulePlateforme = z.strictObject({
  actif: z.boolean(),
  motif: z.string().trim().min(3, 'Motif obligatoire').max(500),
});
export type ModificationModulePlateforme = z.infer<typeof schemaModificationModulePlateforme>;

export interface EtatModuleCollectivite {
  collectivite_id: string;
  actif: boolean;
  motif: string | null;
  modifie_le: string | null;
}

export interface ModulePlateforme {
  module: CodeModule;
  libelle: string;
  fiche: FicheModule;
  /** Activation globale : désactivé ici, le module est inactif partout, quel que soit le réglage local. */
  actif_plateforme: boolean;
  motif: string | null;
  modifie_le: string | null;
  collectivites: EtatModuleCollectivite[];
}

export interface ModulesPlateforme {
  collectivites: { id: string; nom: string; code_court: string; type: string }[];
  elements: ModulePlateforme[];
}
